import LsdApiModel                                         from './global/LsdApiModel';
import { computed }                                        from 'mobx';
import { toJS }                                            from 'mobx';
import { getLabel as getExpenseStatusLabel }               from '../constants/ExpenseStatus';
import { getLabel as getExpensePaymentStatusLabel }        from '../constants/ExpensePaymentStatus';
import { PaymentMethodsReferences }                        from 'constants/PaymentMethods';
import moment                                              from 'moment';
import ExpensePaymentVoucherAccountingDocumentExpenseModel from 'models/ExpensePaymentVoucherAccountingDocumentExpenseModel';
import ExpensePaymentVoucherModel                          from 'models/ExpensePaymentVoucherModel';
import ExpensePaymentVoucherStatus                         from 'constants/ExpensePaymentVoucherStatus';
import AccountingDocumentExpenseModel                      from 'models/AccountingDocumentExpenseModel';
import AccountingDocumentModel                             from 'models/AccountingDocumentModel';
import CurrencyModel                                       from 'models/CurrencyModel';
import { convertFromCurrencyToCurrency }                   from 'tools/currencyTools';

export default class ExpenseModel extends LsdApiModel {
	static get path(): string {
		return '/expenses/{?id}';
	}

	@computed
	public get accountingDocumentExpenses() {
		return toJS(this.get('accountingDocumentExpenses', []));
	}

	@computed
	public get amount() {
		return this.get('amount', 0);
	}

	@computed
	public get amountInCompanyCurrency() {
		return this.get('amountInCompanyCurrency');
	}

	@computed
	public get applicantId() {
		return this.get('applicant.id');
	}

	@computed
	public get applicantIri() {
		return this.get('applicant.@id');
	}

	@computed
	public get currentResponsibles() {
		return this.get('currentResponsibles', []);
	}

	@computed
	public get currentResponsiblesIds() {
		return this.currentResponsibles.map(data => data.id);
	}

	@computed
	public get closed() {
		return this.get('closed', false);
	}

	@computed
	public get comment() {
		return this.get('comment');
	}

	@computed
	public get createdAt() {
		return this.get('createdAt');
	}

	@computed
	public get createdAtMoment() {
		return moment(this.createdAt);
	}

	@computed
	public get createdById() {
		return this.get('createdBy.id');
	}

	@computed
	public get createdByIri() {
		return this.get('createdBy.@id');
	}

	@computed
	public get currencyIri() {
		return this.get('currency.@id');
	}

	@computed
	public get description() {
		return this.get('description');
	}

	@computed
	get documents() {
		return this.get('documents', []);
	}

	@computed
	get expenseReportFor() {
		return this.get('expenseReportFor');
	}

	@computed
	public get expenseReportForIri() {
		return this.get('expenseReportFor.@id');
	}

	@computed
	public get headers() {
		return this.get('headers');
	}

	@computed
	get numberOfDeadlines() {
		return this.get('numberOfDeadlines', 1);
	}

	@computed
	get paymentMethod() {
		return this.get('paymentMethod');
	}

	@computed
	get paymentMethodLabel() {
		if (!this.paymentMethod) {
			return '';
		}

		return PaymentMethodsReferences[this.paymentMethod].label;
	}

	@computed
	get paymentStatus() {
		return this.get('paymentStatus');
	}

	@computed
	get paymentStatusLabel() {
		return getExpensePaymentStatusLabel(this.paymentStatus);
	}

	@computed
	get previousYearAmount() {
		return this.get('previousYearAmount', 0);
	}

	@computed
	get previousYearAmountInCompanyCurrency() {
		return this.get('previousYearAmountInCompanyCurrency', 0);
	}

	@computed
	get previousYearExpenseId() {
		return this.get('previousYearExpense.id');
	}

	@computed
	get previousYearExpenseIri() {
		return this.get('previousYearExpense.@id');
	}

	@computed
	get projectId() {
		return this.get('project.id');
	}

	@computed
	get projectIri() {
		return this.get('project.@id');
	}

	@computed
	get priority() {
		return this.get('priority', false);
	}

	@computed
	public get reason() {
		return this.get('reason');
	}

	@computed
	get recurrent() {
		return this.get('recurrent', false);
	}

	@computed
	get status() {
		return this.get('status');
	}

	@computed
	get statusLabel() {
		return getExpenseStatusLabel(this.status);
	}

	@computed
	get withDeposit() {
		return this.get('withDeposit', false);
	}

	@computed
	get title() {
		return this.get('title');
	}

	public getAmounts = (
		accountingDocumentCollection,
		accountingDocumentExpenseCollection,
		companyCurrency,
		currencyCollection,
		expensePaymentVoucherAccountingDocumentExpenseCollection,
		expensePaymentVoucherCollection,
		getCurrencyByIri,
	) => {
		const {
			amountPaid,
			amountToPayForNotLinkedVouchers,
		} = expensePaymentVoucherCollection
			.filter((epv: ExpensePaymentVoucherModel) => epv.expenseId === this.id)
			.reduce(
				(acc, epv: ExpensePaymentVoucherModel) => {
					const linked = !!expensePaymentVoucherAccountingDocumentExpenseCollection.find(
						(epvAde: ExpensePaymentVoucherAccountingDocumentExpenseModel) => epvAde.expensePaymentVoucherId === epv.id
					);

					if (epv.status === ExpensePaymentVoucherStatus.PAID.reference) {
						acc.amountPaid += epv.amountPaidInCompanyCurrency;
					} else if (
						epv.status !== ExpensePaymentVoucherStatus.REFUSED.reference
						&& !linked
					) {
						acc.amountToPayForNotLinkedVouchers += epv.amountInCompanyCurrency;
					}

					return acc;
				},
				{
					amountPaid: 0,
					amountToPayForNotLinkedVouchers: 0,
				}
			);

		const accountingDocumentExpensesAmountInCompanyCurrency = accountingDocumentExpenseCollection
			.filter((ade: AccountingDocumentExpenseModel) => ade.expenseId === this.id)
			.reduce(
				(totalAmountInCompanyCurrency, accountingDocumentExpense: AccountingDocumentExpenseModel) => {
					const accountingDocumentFound = accountingDocumentCollection.find(
						(accountingDocument: AccountingDocumentModel) => {
							return accountingDocument.iri === accountingDocumentExpense.accountingDocumentIri;
						}
					) as AccountingDocumentModel;

					if (!accountingDocumentFound) {
						return totalAmountInCompanyCurrency;
					}

					const accountingDocumentFoundCurrencyFound = getCurrencyByIri(
						accountingDocumentFound.currencyIri
					) as CurrencyModel;

					if (!accountingDocumentFoundCurrencyFound) {
						return totalAmountInCompanyCurrency;
					}

					const amountInCompanyCurrency = convertFromCurrencyToCurrency(
						accountingDocumentFoundCurrencyFound,
						companyCurrency,
						currencyCollection,
						accountingDocumentExpense.amount
					);

					return totalAmountInCompanyCurrency + amountInCompanyCurrency;
				},
				0
			);

		const amountLeftToPayInCompanyCurrency = accountingDocumentExpensesAmountInCompanyCurrency - amountPaid + amountToPayForNotLinkedVouchers;

		return {
			accountingDocumentExpensesAmountInCompanyCurrency,
			amountPaid,
			amountToPayForNotLinkedVouchers,
			amountLeftToPayInCompanyCurrency,
		};
	}
}
